import { Transition } from '@headlessui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { Fragment, useMemo } from 'react';

import clsxm from '@/lib/clsxm';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

import { getPresetToolHref, presets } from '@/constants/presets';
import { findActiveByPath, toolCategories } from '@/constants/toolCategories';

import { navigation } from './DashboardLayout';
import NavBadge from './NavBadge';
import NavPinButton from './NavPinButton';

interface ToolsFlyoutProps {
  isOpen: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const ToolsFlyout: React.FC<ToolsFlyoutProps> = ({
  isOpen,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const active = findActiveByPath(router.asPath);
  const { favoriteTools, toggleFavorite } = useFavoriteTools();

  const favoritePresets = useMemo(() => {
    // Get all navigation item slugs to exclude
    const navigationSlugs = navigation.map((item) => item.slug);

    return favoriteTools
      .filter((slug) => !navigationSlugs.includes(slug)) // Exclude navigation items
      .slice(0, 10) // Top 10
      .map((slug) => presets.find((p) => p.slug === slug))
      .filter(
        (preset): preset is (typeof presets)[number] => preset !== undefined,
      ); // Remove undefined if preset not found
  }, [favoriteTools]);

  return (
    <>
      <Transition
        show={isOpen}
        as={Fragment}
        enter='transition ease-out duration-300'
        enterFrom='opacity-0 -translate-y-2'
        enterTo='opacity-100 translate-y-0'
        leave='transition ease-in duration-200'
        leaveFrom='opacity-100 translate-y-0'
        leaveTo='opacity-0 -translate-y-2'
      >
        <div
          className='w-[920px]'
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div className='relative overflow-hidden rounded-2xl border border-gray-200/50 bg-gradient-to-br from-stone-50 via-white to-stone-50 shadow-2xl backdrop-blur-sm dark:border-slate-700/50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900'>
            {/* Subtle noise texture overlay */}
            <div
              className='pointer-events-none absolute inset-0 opacity-[0.015] mix-blend-overlay'
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
              }}
            />

            {/* Category Grid */}
            <div className='relative grid grid-cols-4 gap-3 p-6'>
              {toolCategories.map((category, categoryIdx) => (
                <div
                  key={category.title}
                  className='group relative'
                  style={{
                    animation: `fadeInUp 0.4s ease-out ${categoryIdx * 0.08}s both`,
                  }}
                >
                  {/* Category Card */}
                  <div
                    className={clsxm(
                      'relative rounded-xl bg-gradient-to-br p-4 shadow-sm transition-all duration-300',
                      'hover:shadow-xl hover:-translate-y-1',
                      category.color,
                    )}
                  >
                    {/* Category Title */}
                    <div className='mb-4 flex items-center gap-2'>
                      <category.icon className='h-5 w-5 flex-shrink-0 text-gray-700 dark:text-slate-300' />
                      <h3 className='font-primary text-base font-semibold tracking-tight text-gray-800 dark:text-slate-200'>
                        {t(category.title)}
                      </h3>
                    </div>

                    {/* Tools List */}
                    <ul className='space-y-0.5'>
                      {category.tools.map((tool) => {
                        const isActive = active?.tool.slug === tool.slug;

                        return (
                          <li key={tool.name}>
                            <Link
                              href={tool.href}
                              className='group/item relative flex items-center gap-2 rounded-lg py-2 text-sm font-normal transition-all duration-200'
                            >
                              <span
                                className={clsxm(
                                  'flex-1 font-primary transition-colors',
                                  isActive
                                    ? 'font-medium text-primary-600 dark:text-primary-400'
                                    : 'text-gray-700 group-hover/item:text-primary-500 dark:text-slate-400 dark:group-hover/item:text-primary-400',
                                )}
                              >
                                {t(tool.name)}
                              </span>
                              {tool.badge && (
                                <span className='flex-shrink-0'>
                                  <NavBadge type={tool.badge} />
                                </span>
                              )}
                              <NavPinButton
                                slug={tool.slug}
                                isFavorite={favoriteTools.includes(tool.slug)}
                                onToggleFavorite={toggleFavorite}
                                showOnHover={true}
                                alwaysShowIfFavorite={true}
                              />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Curated Favorites Section */}
            {favoritePresets.length > 0 && (
              <div
                className='border-t border-gray-200/50 bg-gradient-to-r from-amber-50/50 via-yellow-50/30 to-amber-50/50 px-6 py-5 dark:border-slate-700/50 dark:from-amber-900/10 dark:via-yellow-900/5 dark:to-amber-900/10'
                style={{ animation: 'fadeInUp 0.4s ease-out 0.32s both' }}
              >
                <div className='mb-3 flex items-center gap-2'>
                  <svg
                    className='h-4 w-4 text-amber-600 dark:text-amber-400'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                  </svg>
                  <h3 className='font-primary text-sm font-semibold text-gray-800 dark:text-slate-200'>
                    {t('Your Favorites')}
                  </h3>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {favoritePresets.map((preset, idx) => (
                    <Link
                      key={preset.slug}
                      href={getPresetToolHref(preset)}
                      className='group/fav inline-flex items-center gap-2 rounded-full border border-amber-200/60 bg-white/80 px-4 py-2 text-sm font-normal shadow-sm transition-all duration-200 hover:border-amber-300 hover:bg-white hover:shadow-md dark:border-amber-800/40 dark:bg-slate-800/60 dark:hover:border-amber-700 dark:hover:bg-slate-800'
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${0.4 + idx * 0.05}s both`,
                      }}
                    >
                      <preset.icon className='h-3.5 w-3.5 text-amber-600 transition-transform group-hover/fav:scale-110 dark:text-amber-400' />
                      <span className='font-primary text-gray-700 transition-colors group-hover/fav:text-gray-900 dark:text-slate-400 dark:group-hover/fav:text-slate-300'>
                        {t(preset.title)}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Elegant Footer */}
            <div
              className='border-t border-gray-200/50 bg-gradient-to-r from-stone-50 to-stone-100 px-6 py-4 dark:border-slate-700/50 dark:from-slate-900 dark:to-slate-800'
              style={{ animation: 'fadeInUp 0.4s ease-out 0.4s both' }}
            >
              <Link
                href='/presets'
                className='group/footer inline-flex items-center gap-2 font-primary text-sm font-medium text-gray-700 transition-all duration-200 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200'
              >
                <span className='transition-transform group-hover/footer:translate-x-0.5'>
                  {t('See all tools')}
                </span>
                <svg
                  className='h-4 w-4 transition-all duration-200 group-hover/footer:translate-x-1'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </Transition>

      {/* Keyframe Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ToolsFlyout;
